import { useEffect, useReducer } from 'react';
import axios from 'axios';
import ProductId from 'src/types/ProductFinder';
import { getProductListSearch, ProductsSearchResponse } from 'src/api/products';
import { FETCH_ACTION_TYPES, fetchReducer } from 'src/reducers/fetchReducer';
import ProductFinderInfo from 'src/types/ProductFinderInfo';
import { Sorting } from 'src/types/Sorting';
import { getProduct, ProductResponse } from 'src/api/product';
import { getInfoInsights, InfoInsightResponse } from 'src/api/insights';
import { getAlerts, AlertsResponse } from 'src/api/alert';
import {
  PRODUCT_LIST_ACTION_TYPES,
  ProductListAction,
  productListReducer,
  ProductListState,
} from '../reducers/productListReducer';
import { getMatchesPriceStats, MatchesPriceStatsResponse } from 'src/api/matchesPriceStats';
import { useProductDetailsStateContext } from 'src/stateProviders/useProductDetailsStateContext';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';

const initialState: ProductListState = {
  loading: false,
  error: false,
  data: null,
  total: 0,
  page: 1,
  offset: 1,
  size: 0,
  sorting: null,
  productKey: '',
  manufacturerIds: null,
  priceVarianceIds: null,
  matchesIds: null,
  ownerIds: null,
  categoryId: null,
  sourceOwnerId: '',
  activeIds: 0,
  noMatchIds: 0,
  insightIds: 0,
  alertIds: 0,
  customFilterListIds: null,
  initialMatchTypeFilterIds: 'ALL',
  initialRegionIds: '-1',
  initialPriceTypeIds: '2',
};

const useProductListFetch = (
  initialSorting: Sorting<ProductFinderInfo>,
  initialPage: number,
  initialSize: number,
  productKey: string,
  initialCategoryId: string,
  initialManufacturerIds: string,
  initialPriceVarianceIds: string,
  initialMatchesIds: string,
  initialOwnerIds: string,
  initialSourceOwnerId: string,
  initialActive: number,
  initialNoMatch: number,
  initialInsight: number,
  initialAlert: number,
  initialCustomFilterListIds: string,
  initialMatchTypeFilter: string,
  initialRegion: string,
  initialPriceType: string
): ProductListState => {
  const [state, dispatch] = useReducer(
    (state: ProductListState, action: ProductListAction) => {
      const fetchState: ProductListState = fetchReducer(state, action) as ProductListState;
      return productListReducer({ ...fetchState }, action);
    },
    {
      ...initialState,
      sorting: initialSorting,
      size: initialSize,
      productKey,
      manufacturerIds: JSON.parse(initialManufacturerIds),
      priceVarianceIds: JSON.parse(initialPriceVarianceIds),
      matchesIds: JSON.parse(initialMatchesIds),
      ownerIds: JSON.parse(initialOwnerIds),
      categoryId: initialCategoryId,
      sourceOwnerId: initialSourceOwnerId,
      activeIds: initialActive,
      noMatchIds: initialNoMatch,
      insightIds: initialInsight,
      alertIds: initialAlert,
      customFilterListIds: JSON.parse(initialCustomFilterListIds),
      initialMatchTypeFilterIds: initialMatchTypeFilter,
      initialRegionIds: initialRegion,
      initialPriceTypeIds: initialPriceType,
    }
  );
  const {
    offset,
    page,
    size,
    sorting,
    manufacturerIds,
    priceVarianceIds,
    matchesIds,
    ownerIds,
    categoryId,
    sourceOwnerId,
    activeIds,
    noMatchIds,
    insightIds,
    alertIds,
    customFilterListIds,
    initialMatchTypeFilterIds,
    initialRegionIds,
    initialPriceTypeIds,
  } = state;

  useEffect(() => {
    if (initialSorting && initialSize && initialPage) {
      dispatch({
        type: PRODUCT_LIST_ACTION_TYPES.updateOptions,
        payload: {
          sorting: initialSorting,
          size: initialSize,
          page: initialPage,
          productKey,
          manufacturerIds: JSON.parse(initialManufacturerIds),
          priceVarianceIds: JSON.parse(initialPriceVarianceIds),
          matchesIds: JSON.parse(initialMatchesIds),
          ownerIds: JSON.parse(initialOwnerIds),
          categoryId: initialCategoryId,
          sourceOwnerId: initialSourceOwnerId,
          activeIds: initialActive,
          noMatchIds: initialNoMatch,
          insightIds: initialInsight,
          alertIds: initialAlert,
          customFilterListIds: JSON.parse(initialCustomFilterListIds),
          initialMatchTypeFilterIds: initialMatchTypeFilter,
          initialRegionIds: initialRegion,
          initialPriceTypeIds: initialPriceType,
        },
      });
    }
  }, [
    initialSorting,
    initialSize,
    initialPage,
    productKey,
    initialManufacturerIds,
    initialPriceVarianceIds,
    initialMatchesIds,
    initialOwnerIds,
    initialCategoryId,
    initialSourceOwnerId,
    initialActive,
    initialNoMatch,
    initialInsight,
    initialAlert,
    initialCustomFilterListIds,
    initialMatchTypeFilter,
    initialRegion,
    initialPriceType,
  ]);

  const sourceProduct = '1';
  const { ownerId = '', matchTypeFilter = 'All', variance = '' } = useProductDetailsStateContext();
  const ownerIdLocal = localStorage.getItem('ownerId') || '1';
  const { user } = useAppStateContext();
  const emailId = user?.userEmail ?? '';
  const hasInsights = user?.appPermissions['enable_insight'];
  const hasAlerts = user?.appPermissions['enable_alert'];

  useEffect(() => {
    const fetch = async () => {
      if (dispatch) {
        const offset = (initialPage - 1) * size + 1;

        dispatch({ type: FETCH_ACTION_TYPES.loading, payload: { loading: true } });
        try {
          const { products, totalRecords }: ProductsSearchResponse = await getProductListSearch(
            sorting,
            offset,
            size,
            productKey,
            categoryId,
            manufacturerIds,
            priceVarianceIds,
            matchesIds,
            ownerIds,
            sourceOwnerId,
            activeIds,
            noMatchIds,
            insightIds,
            alertIds,
            emailId,
            customFilterListIds,
            initialMatchTypeFilterIds,
            initialRegionIds,
            initialPriceTypeIds
          );
         const requests = products.map((productID: ProductId) =>
            getProduct(
              productID,
              sourceProduct,
              sourceOwnerId,
              ownerIdLocal,
              initialMatchTypeFilter,
              variance,
              offset,
              size,
              initialRegionIds,
              initialPriceTypeIds
            )
          );
          const priceStatsReq = products.map((productId: ProductId) =>
            getMatchesPriceStats(sourceOwnerId, productId, initialRegion, initialMatchTypeFilter, initialPriceType)
          );
          let alerts: Promise<AlertsResponse>[] = [];
          if (hasAlerts) {
            alerts = products.map((sourceProductId: ProductId) =>
              getAlerts(emailId, sourceProductId, initialMatchTypeFilter, offset, size, initialRegionIds)
            );
          }
          let insightRequest: Promise<InfoInsightResponse>[] = [];
          if (hasInsights) {
            insightRequest = products.map((sourceProductId: ProductId) =>
              getInfoInsights(
                sourceOwnerId,
                sourceProductId,
                initialMatchTypeFilter,
                offset,
                size,
                initialRegionIds,
                initialPriceTypeIds
              )
            );
          }
          const responsesPriceStats: MatchesPriceStatsResponse[] = await axios.all(priceStatsReq);
          const responsesInsights: InfoInsightResponse[] = hasInsights ? await axios.all(insightRequest) : [];
          const repAlerts: AlertsResponse[] = hasAlerts ? await axios.all(alerts) : [];
          const responses: ProductResponse[] = await axios.all(requests);
          const info: ProductFinderInfo[] = responses.map((response: ProductResponse, index: number) => {
            const priceStats = responsesPriceStats.find(
              (price) => price.priceStats.productID.toString() === products[index]
            );
            
            const getAlert = (productId: string) => {
              let alertsMsg: string[] = [];
              if (hasAlerts) {
                repAlerts.forEach((alerts: any) => {
                  if (alerts[productId]) {
                    const newAlertMsg = alerts[productId].map((alert: any) => alert?.alertName || '');
                    alertsMsg = alertsMsg.concat(...newAlertMsg);
                  }
                });
              }
              return alertsMsg;
            };

            const getInsights = (productId: string) => {
              let ownerInsights: string[] = [];
              let otherInsights: string[] = [];
            
              if (hasInsights) {
                responsesInsights.forEach((insights: any) => {
                  if (insights[productId]) {
                    insights[productId].forEach((insight: any) => {
                      const insightMessages = insight?.insightMessages || [];
                      if (insight?.ownerId === 0) {
                        ownerInsights = ownerInsights.concat(insightMessages.map((msg: any) => `<b>${msg}</b>`));
                      } else {
                        otherInsights = otherInsights.concat(insightMessages);
                      }
                    });
                  }
                });
              }
              const allInsights = [...ownerInsights, ...otherInsights].sort((a, b) => a.localeCompare(b));
            
              return allInsights;
            };
            const productListInfo: ProductFinderInfo = {
              ...response.productDetail,
              ...priceStats,
              matches: priceStats && priceStats.priceStats.noOfMatches,
              key: response.productDetail.ID + '_' + index,
              alerts: getAlert(response.productDetail.ID),
              insights: getInsights(response.productDetail.ID),
            };
            return productListInfo;
          });
          dispatch({
            type: PRODUCT_LIST_ACTION_TYPES.setProducts,
            payload: { data: [...info], total: Number(totalRecords) },
          });
        } catch (e) {
          dispatch({ type: FETCH_ACTION_TYPES.setError, payload: { error: e } });
        }
      }
    };
    if (sourceOwnerId && initialMatchTypeFilterIds && initialRegionIds && initialPriceTypeIds) {
      fetch();
    }
  }, [
    page,
    size,
    sorting,
    productKey,
    offset,
    categoryId,
    manufacturerIds,
    priceVarianceIds,
    matchesIds,
    ownerIds,
    sourceOwnerId,
    activeIds,
    noMatchIds,
    insightIds,
    alertIds,
    customFilterListIds,
    ownerId,
    matchTypeFilter,
    variance,
    initialMatchTypeFilterIds,
    initialRegionIds,
    initialPriceTypeIds,
  ]);

  return state;
};

export default useProductListFetch;
